import {Component, OnInit} from '@angular/core';
import {ProjectResponse} from "../../../models/projects/projectResponse";
import {baseFile} from "../../../models/grantApplication/grantApplicationGetinfoResponse";
import {FormArray, FormControl, FormGroup, Validators} from "@angular/forms";
import {wholeNumberValidator} from "../../../components/validators/whole-number-validator";
import {notBlankValidator} from "../../../components/validators/not-blank-validator";
import {storedMaxLengthValidator} from "../../../components/validators/stored-max-length-validator";
import {englishOnlyValidator} from "../../../components/validators/english-only-validator";
import {
  GRANT_APPLICATION_AMOUNT,
  GRANT_APPLICATION_LIMITS
} from "../../../constants/dataverse-field-limits";
import {CurrencyResponse} from "../../../models/Common/currencyResponse";
import {ActivatedRoute, Router} from "@angular/router";
import {ProjectService} from "../../../services/project.service";
import {NgxSpinnerService} from "ngx-spinner";
import {CommonService} from "../../../services/common.service";
import {GrantApplicationService} from "../../../services/grantApplication.service";
import Swal from "sweetalert2";
import {emergencyFundTargetResponse} from "../../../models/Common/emergencyFundTargetResponse";
import {
  ExistingApplicationInfo
} from "../../../models/grantApplication/grantApplicationResponse";
import swal from "sweetalert2";

export interface EmergencyApplicationFormValue {
  projectId: string;
  budget: number;
  currencyId: string;
  applicationName: string;
  fundingPupose: string;
  costExplanation: string;
  fundingTarget: string;
  grantApplicationId?:string;
}

@Component({
  selector: 'app-emergency-grant-application',
  templateUrl: './emergency-grant-application.component.html',
  styleUrls: ['./emergency-grant-application.component.scss']
})
export class EmergencyGrantApplicationComponent implements OnInit{
  id: string = "";
  url: any;
  projectData: ProjectResponse = new ProjectResponse();
  files: baseFile[] = [];

  fundTarget: emergencyFundTargetResponse[] = [];

  /** Dataverse column constraints - see src/app/constants/dataverse-field-limits.ts */
  protected readonly limits = GRANT_APPLICATION_LIMITS;
  protected readonly amountRange = GRANT_APPLICATION_AMOUNT;

  applicationForm: FormGroup = new FormGroup({
    projectId: new FormControl(''),
    budget: new FormControl('', [
      Validators.required,
      wholeNumberValidator(),
      Validators.min(GRANT_APPLICATION_AMOUNT.min),
      Validators.max(GRANT_APPLICATION_AMOUNT.max)
    ]),
    currencyId: new FormControl('', [Validators.required]),
    applicationName: new FormControl('', [
      Validators.required,
      notBlankValidator(),
      Validators.maxLength(GRANT_APPLICATION_LIMITS.applicationName),
      englishOnlyValidator()
    ]),
    fundingPupose: new FormControl('', [
      Validators.required,
      notBlankValidator(),
      storedMaxLengthValidator(GRANT_APPLICATION_LIMITS.fundingPupose)
    ]),
    costExplanation: new FormControl('', [
      Validators.required,
      notBlankValidator(),
      storedMaxLengthValidator(GRANT_APPLICATION_LIMITS.costExplanation)
    ]),
    fundingTarget: new FormControl('', [Validators.required]),
  });
  protected currency: CurrencyResponse[] = [];

  protected fileForm: FormGroup = new FormGroup({
    documentDatas: new FormArray([]),
  });
  private grantApplicationId?: string;


  constructor(
    private route: ActivatedRoute,
    private projectService: ProjectService,
    private spinner: NgxSpinnerService,
    private commonService: CommonService,
    private grantApplicationService: GrantApplicationService,
    private router: Router,
  ) {
    this.route.params.subscribe(params => {
      this.id = params['appid'];
      this.applicationForm.get('projectId')?.setValue(this.id);
    });
  }

  get documentDatas(): FormArray {
    return this.fileForm.get('documentDatas') as FormArray;
  }

  async ngOnInit(): Promise<void> {

    await this.spinner.show();
    Promise.all([
      this.loadCurrency(),
      this.loadEmergencyFund(),
    ]).finally(() => this.spinner.hide());
    await this.loadProject();

    this.setupCofundingValidation();

    await this.loadExistingApplication();


  }

  private async loadExistingApplication(): Promise<void> {
    try {
      const projectId = this.id;

      if (!projectId) {
        console.warn("No project ID available to fetch existing application.");
        return;
      }


      await this.spinner.show();


      const payload = {
        projectId: projectId,
        isEmergencyApplication: true
      };


      const response = await this.projectService.ExistingApplication(payload).toPromise();


      if (response?.result && response?.item) {
        const existingApplication = response.item;


        if (existingApplication.isEmergencyApplication && existingApplication.emergencyApplicationInfo) {
          this.loadEmergencyApplication(existingApplication);
        }



        console.log("Existing application loaded successfully.");
      } else {
        console.warn("No existing application found.");

      }
    } catch (error) {
      console.error("Error occurred while loading existing application:", error);
    } finally {
      await this.spinner.hide();
    }
  }

  private loadEmergencyApplication(existingApplicationInfo: ExistingApplicationInfo): void {

    const emergencyInfo = existingApplicationInfo.emergencyApplicationInfo;

    this.files = existingApplicationInfo.reportFiles;

    if (!emergencyInfo) return;

    this.grantApplicationId = emergencyInfo.grantApplicationId;

    this.applicationForm.patchValue({
      projectId: emergencyInfo.projectId,
      applicationName: emergencyInfo.applicationName,
      budget: emergencyInfo.budget,
      currencyId: emergencyInfo.currencyId,
      fundingPupose: emergencyInfo.fundingPupose,
      fundingTarget:emergencyInfo.fundingTarget,
      costExplanation:emergencyInfo.costExplanation
    });


    console.log("Emergency application info loaded:", emergencyInfo);
  }

  async loadProject() {
    await this.spinner.show();
    this.projectService.getProject(this.id).subscribe(res => {
      if (res && res.result) {
        this.projectData = res.item ?? new ProjectResponse();

      }
      this.spinner.hide();
    });
  }

  loadCurrency() {
    return new Promise<void>((resolve, reject) => {
      this.commonService.getCurrency().subscribe({
        next: (response) => {
          this.currency = response.item || [];
          resolve();
        },
        error: (error) => {
          console.error("Error loading currency", error);
          reject(error);
        },
      });
    });
  }

  loadEmergencyFund() {
    return new Promise<void>((resolve, reject) => {
      this.commonService.getEmergencyFundingTarget().subscribe({
        next: (response) => {
          this.fundTarget = response.item || [];
          resolve();
        },
        error: (error) => {
          console.error("Error loading emergency funds target", error);
          reject(error);
        },
      });
    });
  }



  clearUploadedFiles(): void {

    while (this.documentDatas.length !== 0) {
      this.documentDatas.removeAt(0);
    }
    console.log('Uploaded files cleared from the form.');
  }
  async onSubmit() {
    const formValue = this.applicationForm.value as EmergencyApplicationFormValue;


    console.log('Invalid fields:');
    Object.keys(this.applicationForm.controls).forEach(field => {
      const control = this.applicationForm.get(field);
      if (control?.invalid) {
        console.log(`${field} is invalid`, control.errors);
      }
    });

    if (this.applicationForm.invalid) {
      Object.keys(this.applicationForm.controls).forEach(field => {
        const control = this.applicationForm.get(field);
        control?.markAsTouched({onlySelf: true});
      });

     await Swal.fire({
        title: 'Form Invalid',
        html: `Please correct the invalid field.`,
        icon: 'warning',
        confirmButtonText: 'OK',
      });
    } else {
      const formData = this.prepareFormData(formValue, this.grantApplicationId);
      await this.spinner.show();

      this.grantApplicationService.createEmergency(formData).subscribe({
        next: async (result) => {
          await this.spinner.hide();
          if (result.result) {
            if (!result.hasWarning) {
              console.log('Document uploaded successfully');
              this.clearUploadedFiles();
              await swal.fire({
                title: "The application has been successfully submitted.",
                icon: 'success',
                confirmButtonText: 'OK',
              }).then(() => {
                this.router.navigate(['/funding-opportunities']);
              });
            } else {
              await swal.fire({
                title: 'Warning',
                text: result.messages?.join('\n') || '',
                icon: 'warning',
                confirmButtonText: 'OK',
              });
            }
          }
          await this.spinner.hide();
        },
        error: async () => {
          // Failure text and the support reference id come from HttpErrorInterceptor.
          await this.spinner.hide();
        },
      });
    }
  }

  async onSaveDraft() {
    const formValue = this.applicationForm.value as EmergencyApplicationFormValue;


    console.log('Invalid fields:');
    Object.keys(this.applicationForm.controls).forEach(field => {
      const control = this.applicationForm.get(field);
      if (control?.invalid) {
        console.log(`${field} is invalid`, control.errors);
      }
    });

    if (this.applicationForm.invalid) {
      Object.keys(this.applicationForm.controls).forEach(field => {
        const control = this.applicationForm.get(field);
        control?.markAsTouched({onlySelf: true});
      });

      await Swal.fire({
        title: 'Form Invalid',
        html: `Please correct the invalid field.`,
        icon: 'warning',
        confirmButtonText: 'OK',
      });
    } else {
      const formData = this.prepareFormData(formValue, this.grantApplicationId);

      await this.spinner.show();

      this.grantApplicationService.saveDraftEmergency(formData).subscribe({
        next: async (result) => {
          await this.spinner.hide();
          if (result.result) {
            if (!result.hasWarning) {
              console.log('Document uploaded successfully');
              this.clearUploadedFiles();
              await swal.fire({
                title: "The draft has been successfully saved.",
                icon: 'success',
                confirmButtonText: 'OK',
              }).then(() => {
                this.router.navigate(['/funding-opportunities']);
              });
            } else {
              await swal.fire({
                title: 'Warning',
                text: result.messages?.join('\n') || '',
                icon: 'warning',
                confirmButtonText: 'OK',
              });
            }
          }
          await this.spinner.hide();
        },
        error: async (_) => {
          await this.spinner.hide();
        },
      });
    }
  }
  private prepareFormData(data: EmergencyApplicationFormValue, grantApplicationId: string|undefined ):FormData {



    const formData = new FormData();


    formData.append('GrantApplicationId', grantApplicationId || "");
    formData.append('ProjectId', data.projectId || '');
    formData.append('Budget', data.budget?.toString() || '');
    formData.append('CurrencyId', data.currencyId || '');

    formData.append('ApplicationName', data.applicationName || '');
    formData.append('fundingPupose', data.fundingPupose || '');
    formData.append('costExplanation', data.costExplanation || '');
    formData.append('fundingTarget', data.fundingTarget || '');
    formData.append('typeOfSupportId', '3');

    this.documentDatas.controls.forEach((control: any) => {
      if (control.value.file) {
        this.appendFileToFormData(formData, control.value.file);
      }
    });

    return formData;
  }

  private appendFileToFormData(formData: FormData, file: File): void {
    formData.append('Files', file, file.name);
  }
  private setupCofundingValidation(): void {
    const cofundStatusIdControl = this.applicationForm.get('cofundStatusId');
    const cofundDescriptionControl = this.applicationForm.get('cofundDescription');

    cofundStatusIdControl?.valueChanges.subscribe((value: string) => {
      if (value === "1") {
        cofundDescriptionControl?.setValidators([Validators.required]);
      } else {
        cofundDescriptionControl?.clearValidators();
      }
      cofundDescriptionControl?.updateValueAndValidity();
    });
  }

}
